import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  LogIn,
  UserPlus,
  Globe,
  LogOut,
  LayoutDashboard,
  Search,
  ChevronDown,
  User,
  Building2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

interface NavbarProps {
  /**
   * Show the marketing page links (Home, About, ...). Record landing pages
   * reuse this header without them, keeping search / language / auth actions.
   */
  showPageLinks?: boolean;
  /**
   * Optional co-branding rendered in the header. Record landing pages pass
   * <BrandMark /> here; everything else leaves it unset and is unchanged.
   * When the mVedra mark is also showing, a light divider is drawn between
   * the two.
   */
  brandSlot?: React.ReactNode;
  /**
   * Drop the mVedra logo so the brandSlot carries the header on its own, and
   * tighten the bottom padding. Used by branded record landing pages, where
   * the owning organisation's mark replaces ours rather than sitting beside it.
   */
  brandOnly?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  showPageLinks = true,
  brandSlot,
  brandOnly = false,
}) => {
  const { isAuthenticated, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showSignupDropdown, setShowSignupDropdown] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const loginHref = "/login";
  const isSignupPage = location.pathname === "/signup";
  const isDashboardPage = location.pathname.startsWith("/dashboard");
  const dashboardHref = isDashboardPage ? "/dashboard/profile" : "/dashboard";
  const dashboardLabel = isDashboardPage ? "Profile" : t("dashboard");

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (!target.closest(".language-dropdown")) {
      setShowLanguageDropdown(false);
    }
    if (!target.closest(".signup-dropdown")) {
      setShowSignupDropdown(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageDropdown, showSignupDropdown]);

  const navigation = [
    { name: t("home"), href: "/" },
    { name: t("about"), href: "/about" },
    { name: t("services"), href: "/services" },
    { name: t("pricing"), href: "/pricing" },
    { name: "Docs", href: "/docs" },
    { name: t("contact"), href: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleLanguageChange = (lang: "en" | "hi") => {
    setLanguage(lang);
    setShowLanguageDropdown(false);
  };

  const getNavbarClasses = () => {
    if (isScrolled) {
      return "bg-white/98 backdrop-blur-md shadow-lg border-b border-neutral-200/40";
    }
    if (isHomePage) {
      return "bg-white/95 backdrop-blur-md border-b border-neutral-200/25";
    }
    if (isSignupPage) {
      return "bg-white/95 backdrop-blur-md border-b border-neutral-200/30";
    }
    return "bg-white/95 backdrop-blur-md shadow-lg border-b border-neutral-200/30";
  };

  return (
    <nav
      className={`site-navbar fixed top-0 left-0 right-0 z-[9999] transition-[background-color,box-shadow,border-color,backdrop-filter] duration-300 ${getNavbarClasses()}`}
    >
      <div className="container-custom">
        <div
          className={`flex justify-between items-center ${
            brandOnly ? 'pt-4 pb-1.5 sm:pt-6 sm:pb-2' : 'py-3 sm:py-4'
          }`}
        >
          {/* Logo, plus optional institution/publisher co-branding */}
          <div className="flex items-center gap-3">
            {!brandOnly && (
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/vedraWebsitelogo.png"
                  alt="mVEDRA Icon"
                  className="h-7 w-auto"
                />
              </Link>
            )}
            {/*
              Co-branding keeps our mark and sets the organisation's beside it,
              separated by a hairline so the two read as distinct marks rather
              than one composite logo. In brandOnly mode there is nothing to
              divide.
            */}
            {brandSlot && !brandOnly && (
              <span
                aria-hidden="true"
                className="h-8 w-px shrink-0 bg-neutral-300"
              />
            )}
            {brandSlot}
          </div>

          {/* Desktop Navigation */}
          {showPageLinks && (
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`font-medium transition-colors duration-200 font-inter ${
                    isActive(item.href)
                      ? "text-vedra-hunter"
                      : "text-neutral-700 hover:text-vedra-hunter"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/search"
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-neutral-700 hover:text-vedra-hunter hover:bg-neutral-100 transition-colors"
              title="Search uploads"
            >
              <Search className="w-5 h-5" />
              <span className="text-sm font-medium">Search</span>
            </Link>
            {/* Language Toggle */}
            <div className="relative language-dropdown">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-neutral-700 hover:text-vedra-hunter hover:bg-neutral-100 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {language === "en" ? "EN" : "HI"}
                </span>
              </button>

              {showLanguageDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => handleLanguageChange("en")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                      language === "en"
                        ? "text-vedra-hunter bg-gray-50"
                        : "text-gray-700"
                    }`}
                  >
                    {t("english")}
                  </button>
                  <span
                    className="w-full block px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    Hindi: Coming Soon
                  </span>
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <button
                onClick={logout}
                className="btn-secondary h-12 items-center justify-center flex cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("signOut")}
              </button>
            ) : (
              <a
                href={loginHref}
                className="btn-secondary h-12 items-center justify-center flex"
              >
                <LogIn className="w-4 h-4 mr-2" />
                {t("signIn")}
              </a>
            )}
            {isAuthenticated ? (
              <Link
                to={dashboardHref}
                className="btn-primary h-12 items-center justify-center flex"
              >
                {isDashboardPage ? (
                  <User className="w-4 h-4 mr-2" />
                ) : (
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                )}
                {dashboardLabel}
              </Link>
            ) : (
              <div className="relative signup-dropdown">
                <button
                  onClick={() => setShowSignupDropdown(!showSignupDropdown)}
                  className="btn-primary h-12 items-center justify-center flex gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {t("signUp")}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      showSignupDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showSignupDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 overflow-hidden">
                    <p className="px-4 pt-1 pb-2 text-xs font-semibold uppercase tracking-widest text-gray-400 font-inter">
                      Create an account
                    </p>
                    <Link
                      to="/register"
                      onClick={() => setShowSignupDropdown(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-primary-50 transition-colors group"
                    >
                      <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-white transition-colors">
                        <User className="w-4 h-4 text-vedra-hunter" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-gray-800 font-inter">
                          Individual Account
                        </span>
                        <span className="block text-xs text-gray-500 font-inter mt-0.5">
                          Researchers, students and faculty
                        </span>
                      </span>
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setShowSignupDropdown(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-primary-50 transition-colors group"
                    >
                      <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-white transition-colors">
                        <Building2 className="w-4 h-4 text-vedra-hunter" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-gray-800 font-inter">
                          Institution Account
                        </span>
                        <span className="block text-xs text-gray-500 font-inter mt-0.5">
                          Universities, colleges, and publishers
                        </span>
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-3 rounded-md text-neutral-700 hover:text-vedra-hunter hover:bg-neutral-100"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div
              className={`px-4 pt-3 pb-4 space-y-1 sm:px-6 border-t bg-white/80 backdrop-blur-md border-neutral-200/30`}
            >
              {showPageLinks &&
                navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-4 py-3 rounded-md text-base font-medium transition-colors duration-200 font-inter ${
                      isActive(item.href)
                        ? "text-vedra-hunter bg-primary-50"
                        : "text-neutral-700 hover:text-vedra-hunter hover:bg-neutral-100"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              <Link
                to="/search"
                className="flex items-center space-x-2 px-4 py-3 rounded-md text-base font-medium text-vedra-hunter hover:bg-primary-50"
                onClick={() => setIsOpen(false)}
              >
                <Search className="w-5 h-5" />
                <span>Search uploads</span>
              </Link>
              {/* Mobile Language Toggle */}
              <div className="pt-4 border-t border-gray-200">
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {t("language")}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleLanguageChange("en")}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          language === "en"
                            ? "bg-vedra-hunter text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        EN
                      </button>
                      <span
                        className="px-3 py-1 rounded text-sm font-medium text-gray-400 cursor-not-allowed"
                        aria-disabled="true"
                      >
                        Hindi: Coming Soon
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 space-y-3">
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      logout();
                    }}
                    className="block w-full text-left px-4 py-3 rounded-md text-base font-medium text-vedra-hunter hover:bg-primary-50 font-inter"
                  >
                    {t("signOut")}
                  </button>
                ) : (
                  <a
                    href={loginHref}
                    className="block px-4 py-3 rounded-md text-base font-medium text-vedra-hunter hover:bg-primary-50 font-inter"
                    onClick={() => setIsOpen(false)}
                  >
                    {t("signIn")}
                  </a>
                )}
                {isAuthenticated ? (
                  <Link
                    to={dashboardHref}
                    className="block px-4 py-3 rounded-md text-base font-medium bg-vedra-hunter text-white hover:bg-vedra-calpoly font-inter"
                    onClick={() => setIsOpen(false)}
                  >
                    {isDashboardPage ? (
                      <User className="w-4 h-4 mr-2 inline-block" />
                    ) : (
                      <LayoutDashboard className="w-4 h-4 mr-2 inline-block" />
                    )}
                    {dashboardLabel}
                  </Link>
                ) : (
                  <div className="space-y-2">
                    <p className="px-4 text-xs font-semibold uppercase tracking-widest text-gray-400 font-inter">
                      Create an account
                    </p>
                    <Link
                      to="/register"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-md bg-vedra-hunter text-white hover:bg-vedra-calpoly font-inter transition-colors"
                    >
                      <User className="w-4 h-4 flex-shrink-0" />
                      <span>
                        <span className="block text-sm font-semibold">
                          Individual / Student
                        </span>
                        <span className="block text-xs opacity-75">
                          Researchers and students
                        </span>
                      </span>
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-md border border-vedra-hunter text-vedra-hunter hover:bg-primary-50 font-inter transition-colors"
                    >
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      <span>
                        <span className="block text-sm font-semibold">
                          Institution / Publisher
                        </span>
                        <span className="block text-xs text-gray-500">
                          Universities, journals, and organisations
                        </span>
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
